import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { technicalReportTokens } from "../../lib/database";
import TechnicalView from "../customer-detail/components/TechnicalView";

/**
 * Public page for viewing technical work data via token link (e.g. from Excel export).
 * No login required; security is the unguessable token (UUID) stored in Supabase.
 */
const TechnicalReportPublic = () => {
  const { token } = useParams();
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");
  const [customer, setCustomer] = useState(null);
  const [technicalWork, setTechnicalWork] = useState(null);

  useEffect(() => {
    const rawToken = token != null ? String(token).trim() : "";
    const decodedToken = rawToken ? decodeURIComponent(rawToken) : "";
    if (!decodedToken) {
      setStatus("error");
      setMessage("Invalid link.");
      return;
    }

    technicalReportTokens
      .getReportByToken(decodedToken)
      .then(({ data, error: rpcError }) => {
        if (rpcError) {
          setStatus("error");
          const msg = rpcError?.message || String(rpcError);
          setMessage(
            msg.includes("does not exist")
              ? "Report link setup incomplete. Please run the technical report tokens SQL in Supabase."
              : msg,
          );
          return;
        }
        if (!data?.customer || !data?.technicalWork) {
          setStatus("error");
          setMessage("Report not found or link invalid.");
          return;
        }
        setCustomer(data.customer);
        setTechnicalWork(data.technicalWork);
        setStatus("success");
      })
      .catch((err) => {
        setStatus("error");
        setMessage(err?.message || "Failed to load technical report.");
      });
  }, [token]);

  if (status === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full text-center">
          <p className="text-gray-600">Loading technical report...</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full text-center">
          <p className="text-red-600 font-medium">{message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="mb-4">
          <h1 className="text-lg md:text-xl font-semibold text-foreground">
            Technical Work Report
          </h1>
          <p className="text-sm text-muted-foreground">
            {customer?.name ? customer.name : "Customer"}
            {customer?.cin ? ` • CIN: ${customer.cin}` : ""}
            {customer?.usn ? ` • USN: ${customer.usn}` : ""}
          </p>
        </div>

        <div className="bg-background rounded-xl border border-border p-4 md:p-6">
          <TechnicalView customer={customer} technicalWork={technicalWork} />
        </div>
      </div>
    </div>
  );
};

export default TechnicalReportPublic;

